import { useState } from "react";
import dayjs from "dayjs";
import { Dialog } from "@headlessui/react";
import { ChevronDownIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Modal } from "../../UI/Modal/Modal.tsx";
import { classNames } from "../../UI/Utils/classNames.ts";
import {
  AssignmentTraceDTO,
  TraceCandidateDTO,
} from "../../HubSpot/useGetSchedulerAssignments.ts";
import { formatRate } from "../Stats/closerStatsFormat.ts";
import {
  CHANNEL_LABELS,
  chosenNameOf,
  freeCandidatesOf,
  KIND_LABELS,
  LOST_ON_LABELS,
  OUTCOME_CELL_CLASS_NAMES,
  OUTCOME_LABELS,
  outcomeOf,
  RULE_LABELS,
  stateClassNameOf,
  stateLabelOf,
  STATUS_CLASS_NAMES,
  STATUS_LABELS,
} from "./assignmentOutcome.ts";
import { SlotSelection } from "./AssignmentsHeatmap.tsx";

type Props = {
  selection: SlotSelection | null;
  onClose: () => void;
};

function reasonOf(candidate: TraceCandidateDTO): string {
  if (candidate.status === "ranked_lower" && candidate.lostOn) {
    return LOST_ON_LABELS[candidate.lostOn];
  }
  if (candidate.status === "cap_reached") {
    return `${candidate.callsOnDay ?? "—"}/${candidate.capacity ?? "—"} call oggi`;
  }
  return "";
}

function capSkippedNote(trace: AssignmentTraceDTO): string | null {
  if (outcomeOf(trace) !== "cap_skipped") return null;
  const ideal = trace.candidates.find(
    (c) => c.closerId === trace.idealCloserId,
  );
  return ideal
    ? `Primo in classifica: ${ideal.name ?? ideal.closerId}, saltato perché al cap (${ideal.callsOnDay}/${ideal.capacity} call quel giorno).`
    : null;
}

function TraceCard({
  trace,
  initiallyOpen,
}: {
  trace: AssignmentTraceDTO;
  initiallyOpen: boolean;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  const outcome = outcomeOf(trace);
  const candidates = freeCandidatesOf(trace);
  const note = capSkippedNote(trace);
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex flex-wrap items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-gray-50"
      >
        <div className="min-w-0">
          <p className="text-sm font-semibold truncate">
            Scelto: {chosenNameOf(trace)}
          </p>
          <p className="text-xs text-gray-500">
            {KIND_LABELS[trace.kind]} · {CHANNEL_LABELS[trace.channel]} ·{" "}
            {dayjs(trace.slotStart).tz("Europe/Rome").format("HH:mm")}–
            {dayjs(trace.slotEnd).tz("Europe/Rome").format("HH:mm")} ·{" "}
            {candidates.length}{" "}
            {candidates.length === 1 ? "candidato libero" : "candidati liberi"}
          </p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
          <span
            className={classNames(
              "text-xs px-2 py-0.5 rounded",
              stateClassNameOf(trace),
            )}
          >
            {stateLabelOf(trace)}
          </span>
          <span
            className={classNames(
              "text-xs px-2 py-0.5 rounded",
              OUTCOME_CELL_CLASS_NAMES[outcome],
            )}
          >
            {OUTCOME_LABELS[outcome]}
          </span>
          <ChevronDownIcon
            className={classNames(
              "w-4 h-4 text-gray-400 transition-transform",
              open ? "rotate-180" : "",
            )}
          />
        </div>
      </button>
      {open && (
        <div className="border-t border-gray-200 px-3 py-2 space-y-2">
          <p className="text-xs text-gray-500">
            Decisa il{" "}
            {dayjs(trace.assignedAt).tz("Europe/Rome").format("DD/MM HH:mm")}
          </p>
          <p className="text-xs text-gray-600">
            Regola: {RULE_LABELS[trace.rule]}
            {trace.overBusy ? ", anche sopra i suoi impegni Google" : ""}.
          </p>
          {note && <p className="text-xs text-amber-700">{note}</p>}
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-left text-gray-400 border-b border-gray-100">
                  <th className="py-1 pr-2 font-medium">#</th>
                  <th className="py-1 pr-2 font-medium">Closer</th>
                  <th className="py-1 pr-2 font-medium">Stato</th>
                  <th className="py-1 pr-2 font-medium">Motivo</th>
                  <th className="py-1 pr-2 font-medium text-right">Call/cap</th>
                  <th className="py-1 pr-2 font-medium text-right">CR</th>
                  <th className="py-1 font-medium text-right">Tier</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c) => (
                  <tr key={c.closerId} className="border-b border-gray-50">
                    <td className="py-1 pr-2 tabular-nums text-gray-400">
                      {c.rank ?? "—"}
                    </td>
                    <td className="py-1 pr-2 font-medium">
                      {c.name ?? c.closerId}
                      {c.inTrial && (
                        <span className="ml-1 text-blue-600 font-normal">
                          (prova)
                        </span>
                      )}
                    </td>
                    <td className="py-1 pr-2">
                      <span
                        className={classNames(
                          "px-1.5 py-0.5 rounded",
                          STATUS_CLASS_NAMES[c.status],
                        )}
                      >
                        {STATUS_LABELS[c.status]}
                      </span>
                    </td>
                    <td className="py-1 pr-2 text-gray-500">{reasonOf(c)}</td>
                    <td className="py-1 pr-2 text-right tabular-nums">
                      {c.callsOnDay ?? "—"}/{c.capacity ?? "—"}
                    </td>
                    <td className="py-1 pr-2 text-right tabular-nums">
                      {formatRate(c.cr)}
                    </td>
                    <td className="py-1 text-right tabular-nums">
                      {c.inTrial ? "Prova" : (c.tierRank ?? "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function AssignmentSlotModal({ selection, onClose }: Props) {
  return (
    <Modal
      isOpen={selection !== null}
      onClose={onClose}
      title={selection?.label ?? ""}
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="relative w-full max-w-3xl transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <button
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="absolute top-3 right-3 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <h3 className="text-lg font-medium leading-6 text-gray-900 pr-8 capitalize">
            {title}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Clicca un'assegnazione per vedere i closer liberi nello slot, tra
            cui lo scheduler ha scelto, in ordine di classifica.
          </p>
          <div className="mt-4">{children}</div>
        </Dialog.Panel>
      )}
    >
      <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
        {(selection?.traces ?? []).map((trace) => (
          <TraceCard
            key={`${trace.bookingId}-${trace.assignedAt}`}
            trace={trace}
            initiallyOpen={(selection?.traces.length ?? 0) === 1}
          />
        ))}
      </div>
    </Modal>
  );
}
